/**
 * Trade Image Capture Configuration Panel (KAN-82 Phase 2)
 *
 * Self-contained admin component for configuring trade image capture.
 * Supports LLM-bootstrapped configuration: paste a screenshot and the AI
 * proposes field mappings with bounding-box coordinates for visual review.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { tradeImageCaptureService } from '../../services/api/tradeImageCaptureService';
import { groupingsService } from '../../services/api/groupingsService';
import { GroupingsConfig } from '../../types/groupings';
import AlertModal from '../common/AlertModal';
import ImageHighlightOverlay, { ImageFieldHighlight } from './ImageHighlightOverlay';
import type {
  TradeImageCaptureConfig,
  AnalysedFieldMapping,
  AnalysedValueTransform,
  SourceAnalysisResult,
  FieldCoordinates,
} from '../../types/tradeImageCapture';
import { TRADE_DOCUMENT_FIELDS, TRADE_FIELD_ENUMS } from '../../constants/tradeFields';
import MultiTradeConfigPanel from './MultiTradeConfigPanel';
import './TradeImageCaptureConfigPanel.css';

const TRANSFORM_TYPES = [
  { value: 'DATE_FORMAT', label: 'Date Format' },
  { value: 'ENUM_MAP', label: 'Enum Map' },
  { value: 'NUMBER_FORMAT', label: 'Number Format' },
  { value: 'TEXT_REPLACE', label: 'Text Replace' },
  { value: 'FIELD_SPLIT', label: 'Field Split' },
];

// Mapping row combines field mapping + optional value transform
interface MappingRow {
  sourceLabel: string;
  targetField: string;
  description?: string;
  confidence: number;
  coordinates?: FieldCoordinates;
  isManual: boolean;
  confirmed: boolean;
  // Inline transform (optional)
  transformType?: string;
  transformConfig?: Record<string, any>;
}

type PanelMode = 'loading' | 'empty' | 'analysing' | 'draft' | 'edit';

type SubTab = 'single' | 'multi';

const TradeImageCaptureConfigPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  // KAN-125: Sub-tab state (Single Trade / Multiple Trades)
  const [subTab, setSubTab] = useState<SubTab>('single');

  // Panel mode
  const [mode, setMode] = useState<PanelMode>('loading');

  // Config state
  const [sourceSystemName, setSourceSystemName] = useState('');
  const [mappingRows, setMappingRows] = useState<MappingRow[]>([]);

  // Groupings config (for dynamic field labels)
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  // Image state (for analysis mode and persisted source image)
  const [capturedImageDataUrl, setCapturedImageDataUrl] = useState<string | null>(null);
  const [capturedImageBase64, setCapturedImageBase64] = useState<string | null>(null);
  const [capturedImageMimeType, setCapturedImageMimeType] = useState<string | null>(null);
  const [naturalImageSize, setNaturalImageSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [displayedImageSize, setDisplayedImageSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const [hasPersistedImage, setHasPersistedImage] = useState(false);

  // Selection — only show highlight for clicked row
  const [selectedField, setSelectedField] = useState<string | null>(null);

  // UI state
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  // Adding new mapping
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [newMappingSource, setNewMappingSource] = useState('');
  const [newMappingTarget, setNewMappingTarget] = useState('');
  const [newMappingDescription, setNewMappingDescription] = useState('');

  // Editing existing mapping
  const [editingMappingIndex, setEditingMappingIndex] = useState<number | null>(null);

  // Expanded row (for inline transform editing)
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);

  const showSuccess = (message: string) => {
    setAlertModal({ isOpen: true, type: 'success', title: t('common.success'), message });
  };

  const showError = (message: string) => {
    setAlertModal({ isOpen: true, type: 'error', title: t('common.error'), message });
  };

  // Build FX+ fields list with dynamic grouping labels
  const fxPlusFields = useMemo(() => {
    const fields = TRADE_DOCUMENT_FIELDS.map((f) => ({ value: f.field, label: t(f.labelKey) }));
    const g1Label = groupingsConfig?.grouping1Label || 'Grouping 1';
    const g2Label = groupingsConfig?.grouping2Label || 'Grouping 2';
    fields.push({ value: 'grouping1', label: g1Label });
    fields.push({ value: 'grouping2', label: g2Label });
    if (groupingsConfig?.grouping3Label) {
      fields.push({ value: 'grouping3', label: groupingsConfig.grouping3Label });
    }
    return fields;
  }, [groupingsConfig, t]);

  // Build dynamic enum values (shared TRADE_FIELD_ENUMS + grouping values from config)
  const allEnums = useMemo(() => {
    const enums: Record<string, { value: string; label: string }[]> = {};
    for (const [field, values] of Object.entries(TRADE_FIELD_ENUMS)) {
      enums[field] = values.map((v) => ({ value: v.value, label: t(v.labelKey) }));
    }
    if (groupingsConfig?.grouping1Values?.length) {
      enums.grouping1 = groupingsConfig.grouping1Values
        .filter((v) => v.isActive)
        .map((v) => ({ value: v.name, label: v.name }));
    }
    if (groupingsConfig?.grouping2Values?.length) {
      enums.grouping2 = groupingsConfig.grouping2Values
        .filter((v) => v.isActive)
        .map((v) => ({ value: v.name, label: v.name }));
    }
    if (groupingsConfig?.grouping3Values?.length) {
      enums.grouping3 = groupingsConfig.grouping3Values
        .filter((v) => v.isActive)
        .map((v) => ({ value: v.name, label: v.name }));
    }
    return enums;
  }, [groupingsConfig, t]);

  // Load existing config + groupings on mount (independently so one failure doesn't block the other)
  useEffect(() => {
    if (!clientId) return;

    // Load groupings independently — never let this block config loading
    groupingsService.getGroupings(clientId).then((groupings) => {
      if (groupings) setGroupingsConfig(groupings);
    }).catch((err) => {
      console.error('Failed to load groupings config:', err);
    });

    const loadConfig = async () => {
      try {
        const config = await tradeImageCaptureService.getConfig(clientId);

        if (config) {
          setSourceSystemName(config.sourceSystemName || '');

          // Merge field mappings + value transforms into unified rows
          const rows: MappingRow[] = (config.fieldMappings || []).map((fm) => {
            const matchingTransform = (config.valueTransforms || []).find(
              (vt) => vt.targetField === fm.targetField
            );
            return {
              sourceLabel: fm.sourceLabel,
              targetField: fm.targetField,
              description: fm.description,
              confidence: 1.0,
              coordinates: fm.coordinates,
              isManual: true,
              confirmed: true,
              transformType: matchingTransform?.transformType,
              transformConfig: matchingTransform?.config,
            };
          });
          setMappingRows(rows);

          // Load persisted source image if available
          if (config.sourceImagePath) {
            setHasPersistedImage(true);
            tradeImageCaptureService.getSourceImageUrl(clientId).then((url) => {
              if (url) {
                setCapturedImageDataUrl(url);
                const img = new Image();
                img.onload = () => setNaturalImageSize({ w: img.naturalWidth, h: img.naturalHeight });
                img.src = url;
              }
            }).catch((err) => {
              console.error('Failed to load source image URL:', err);
            });
          }

          setMode('edit');
        } else {
          setMode('empty');
        }
      } catch (err) {
        console.error('Failed to load trade image capture config:', err);
        setMode('empty');
      }
    };

    loadConfig();
  }, [clientId]);

  // Track displayed image size
  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      setNaturalImageSize({
        w: imageRef.current.naturalWidth,
        h: imageRef.current.naturalHeight,
      });
      setDisplayedImageSize({
        w: imageRef.current.clientWidth,
        h: imageRef.current.clientHeight,
      });
    }
  }, []);

  // Update displayed size on resize
  useEffect(() => {
    const handleResize = () => {
      if (imageRef.current) {
        setDisplayedImageSize({
          w: imageRef.current.clientWidth,
          h: imageRef.current.clientHeight,
        });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ------------------------------------------------------------------
  // Screenshot capture (click-to-paste)
  // ------------------------------------------------------------------

  const handleCaptureClick = useCallback(async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        const imageType = clipboardItem.types.find((type) => type.startsWith('image/'));
        if (imageType) {
          const blob = await clipboardItem.getType(imageType);
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataUrl = e.target?.result as string;
            setCapturedImageDataUrl(dataUrl);

            const base64 = dataUrl.split(',')[1];
            setCapturedImageBase64(base64);
            setCapturedImageMimeType(imageType);

            const img = new Image();
            img.onload = () => {
              setNaturalImageSize({ w: img.naturalWidth, h: img.naturalHeight });
            };
            img.src = dataUrl;

            handleAnalyze(base64, imageType, blob.size);
          };
          reader.readAsDataURL(blob);
          return;
        }
      }
      showError('No image found in clipboard. Please copy a screenshot first.');
    } catch (err) {
      showError('Clipboard access is not available in this browser.');
    }
  }, [clientId]);

  const handleAnalyze = async (base64: string, mimeType: string, sizeBytes: number) => {
    setMode('analysing');
    try {
      const result: SourceAnalysisResult = await tradeImageCaptureService.analyzeSource(
        clientId,
        base64,
        mimeType,
        sizeBytes
      );

      // Only set system name if the LLM identified something specific
      const detectedName = (result.sourceSystemName || '').trim();
      const genericNames = ['unknown', 'trading system', 'system', 'source system', 'n/a'];
      if (detectedName && !genericNames.includes(detectedName.toLowerCase())) {
        setSourceSystemName(detectedName);
      }

      // Merge LLM field mappings + value transforms into unified rows
      const rows: MappingRow[] = (result.fieldMappings || []).map((fm) => {
        const matchingTransform = (result.valueTransforms || []).find(
          (vt) => vt.targetField === fm.targetField
        );
        // Keep transform type but strip ENUM_MAP pre-filled mappings —
        // the LLM doesn't have enough info to guess source system values
        let transformConfig = matchingTransform?.config;
        if (matchingTransform?.transformType === 'ENUM_MAP') {
          transformConfig = {};
        }
        return {
          sourceLabel: fm.sourceLabel,
          targetField: fm.targetField,
          description: fm.description,
          confidence: fm.confidence,
          coordinates: fm.coordinates,
          isManual: false,
          confirmed: false,
          transformType: matchingTransform?.transformType,
          transformConfig,
        };
      });

      // Sort by image position: top-to-bottom, then left-to-right
      rows.sort((a, b) => {
        const ay = a.coordinates?.y ?? Infinity;
        const by = b.coordinates?.y ?? Infinity;
        if (Math.abs(ay - by) > 20) return ay - by; // different rows (>20px apart)
        const ax = a.coordinates?.x ?? Infinity;
        const bx = b.coordinates?.x ?? Infinity;
        return ax - bx;
      });

      setMappingRows(rows);
      setMode('draft');
    } catch (err: any) {
      console.error('Source analysis failed:', err);
      showError(err.message || 'Source analysis failed');
      setMode(capturedImageDataUrl ? 'edit' : 'empty');
    }
  };

  // ------------------------------------------------------------------
  // Save
  // ------------------------------------------------------------------

  const handleSave = async () => {
    // Validation
    if (!sourceSystemName.trim()) {
      showError(t('admin.tradeImageCapture.validationSystemName'));
      return;
    }
    const unconfirmed = mappingRows.filter((r) => !r.confirmed);
    if (unconfirmed.length > 0) {
      showError(t('admin.tradeImageCapture.validationAllConfirmed', { count: unconfirmed.length }));
      return;
    }

    setSaving(true);
    try {
      // Split unified rows back into fieldMappings + valueTransforms
      const fieldMappings = mappingRows.map(({ sourceLabel, targetField, description, coordinates }) => ({
        sourceLabel,
        targetField,
        description,
        coordinates,
      }));

      const valueTransforms = mappingRows
        .filter((row) => row.transformType && row.transformConfig)
        .map(({ targetField, transformType, transformConfig }) => ({
          targetField,
          transformType: transformType as any,
          config: transformConfig!,
        }));

      const config: TradeImageCaptureConfig = {
        enabled: true,
        sourceSystemName: sourceSystemName || undefined,
        detectionHints: [],
        fieldMappings,
        valueTransforms,
      };

      // Send image alongside config if we have a new capture (not a persisted image reload)
      await tradeImageCaptureService.saveConfig(
        clientId,
        config,
        capturedImageBase64 || undefined,
        capturedImageMimeType || undefined,
      );
      setHasPersistedImage(true);
      setCapturedImageBase64(null);
      setCapturedImageMimeType(null);
      showSuccess(t('admin.tradeImageCapture.saveSuccess'));
      setMode('edit');
    } catch (err: any) {
      console.error('Failed to save config:', err);
      showError(t('admin.tradeImageCapture.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // Delete entire config
  // ------------------------------------------------------------------

  const handleDeleteConfig = async () => {
    setDeleting(true);
    try {
      await tradeImageCaptureService.deleteConfig(clientId);
      // Reset all state
      setMappingRows([]);
      setSourceSystemName('');
      setCapturedImageDataUrl(null);
      setCapturedImageBase64(null);
      setCapturedImageMimeType(null);
      setHasPersistedImage(false);
      setSelectedField(null);
      setExpandedRowIndex(null);
      setEditingMappingIndex(null);
      setMode('empty');
      showSuccess(t('admin.tradeImageCapture.deleteSuccess'));
    } catch (err: any) {
      console.error('Failed to delete config:', err);
      showError(t('admin.tradeImageCapture.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = () => {
    if (window.confirm(t('admin.tradeImageCapture.deleteConfirmMessage'))) {
      handleDeleteConfig();
    }
  };

  // ------------------------------------------------------------------
  // Mapping rows CRUD
  // ------------------------------------------------------------------

  const addMapping = () => {
    if (!newMappingSource.trim() || !newMappingTarget) return;

    if (mappingRows.some((r) => r.targetField === newMappingTarget)) {
      showError(t('admin.tradeImageCapture.duplicateTargetField'));
      return;
    }

    setMappingRows((prev) => [
      ...prev,
      {
        sourceLabel: newMappingSource.trim(),
        targetField: newMappingTarget,
        description: newMappingDescription.trim() || undefined,
        confidence: 1.0,
        isManual: true,
        confirmed: false,
      },
    ]);
    setNewMappingSource('');
    setNewMappingTarget('');
    setNewMappingDescription('');
    setShowAddMapping(false);
  };

  const removeMapping = (index: number) => {
    if (selectedField === mappingRows[index]?.sourceLabel) {
      setSelectedField(null);
    }
    setMappingRows((prev) => prev.filter((_, i) => i !== index));
    if (expandedRowIndex === index) setExpandedRowIndex(null);
  };

  const updateMappingTarget = (index: number, newTarget: string) => {
    setMappingRows((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const updated: MappingRow = { ...r, targetField: newTarget };
        // Reset transform config when target changes — the old config was for the previous field
        if (r.transformType && r.transformConfig) {
          updated.transformConfig = {};
        }
        return updated;
      })
    );
  };

  const updateRowTransform = (index: number, transformType: string | undefined, transformConfig: Record<string, any> | undefined) => {
    setMappingRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, transformType, transformConfig } : r))
    );
  };

  const toggleConfirmed = (index: number) => {
    setMappingRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, confirmed: !r.confirmed } : r))
    );
  };

  const duplicateMapping = (index: number) => {
    const source = mappingRows[index];
    const isFieldSplit = source.transformType === 'FIELD_SPLIT';
    setMappingRows((prev) => [
      ...prev.slice(0, index + 1),
      {
        sourceLabel: source.sourceLabel,
        targetField: '',
        description: undefined,
        confidence: 1.0,
        coordinates: source.coordinates,
        isManual: true,
        confirmed: false,
        transformType: isFieldSplit ? 'FIELD_SPLIT' : undefined,
        transformConfig: isFieldSplit
          ? { delimiter: source.transformConfig?.delimiter || '-', partIndex: (source.transformConfig?.partIndex ?? 0) + 1 }
          : undefined,
      },
      ...prev.slice(index + 1),
    ]);
    setEditingMappingIndex(index + 1);
  };

  // ------------------------------------------------------------------
  // Image highlights — only show the selected field
  // ------------------------------------------------------------------

  const highlights: ImageFieldHighlight[] = mappingRows
    .filter((fm) => fm.coordinates && selectedField === fm.sourceLabel)
    .map((fm) => ({
      fieldName: fm.sourceLabel,
      coordinates: fm.coordinates as FieldCoordinates,
      confidence: fm.confidence,
      isSelected: true,
    }));

  const handleFieldClick = (sourceLabel: string) => {
    setSelectedField(sourceLabel);
  };

  // ------------------------------------------------------------------
  // Transform config editor helpers
  // ------------------------------------------------------------------

  const renderTransformConfig = (
    transformType: string,
    config: Record<string, any>,
    onChange: (config: Record<string, any>) => void,
    targetField: string,
  ) => {
    switch (transformType) {
      case 'DATE_FORMAT':
        return (
          <div className="transform-config-editor">
            <label>{t('admin.tradeImageCapture.sourceFormat')}:</label>
            <input
              type="text"
              className="form-input config-input"
              value={config.sourceFormat || ''}
              onChange={(e) => onChange({ ...config, sourceFormat: e.target.value })}
              placeholder="DD/MM/YYYY"
            />
          </div>
        );
      case 'ENUM_MAP': {
        const knownEnums = allEnums[targetField];
        const currentMappings: Record<string, string> = config.mappings || {};

        if (knownEnums) {
          // Structured editor: show each FX+ value with an input for source value
          // config.mappings is { sourceValue: fxPlusValue } — reverse it for lookup
          const reverseMappings: Record<string, string> = {};
          Object.entries(currentMappings).forEach(([src, tgt]) => {
            reverseMappings[tgt as string] = src;
          });

          return (
            <div className="transform-config-editor">
              <label>{t('admin.tradeImageCapture.enumMapLabel')}:</label>
              <div className="tic-enum-table">
                <div className="tic-enum-header">
                  <span>{t('admin.tradeImageCapture.fxPlusValue')}</span>
                  <span>{t('admin.tradeImageCapture.sourceSystemValue')}</span>
                </div>
                {knownEnums.map((enumVal) => (
                  <div key={enumVal.value} className="tic-enum-row">
                    <span className="tic-enum-fxplus">{enumVal.label} <span className="tic-enum-code">({enumVal.value})</span></span>
                    <input
                      type="text"
                      className="form-input config-input"
                      value={reverseMappings[enumVal.value] || ''}
                      onChange={(e) => {
                        const newReverse = { ...reverseMappings, [enumVal.value]: e.target.value };
                        const newMappings: Record<string, string> = {};
                        Object.entries(newReverse).forEach(([tgt, src]) => {
                          if (src.trim()) newMappings[src] = tgt;
                        });
                        onChange({ ...config, mappings: newMappings });
                      }}
                      placeholder={t('admin.tradeImageCapture.sourceValuePlaceholder')}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // Fallback for fields without known enums: key-value pair inputs
        const entries = Object.entries(currentMappings);
        return (
          <div className="transform-config-editor">
            <label>{t('admin.tradeImageCapture.mappings')}:</label>
            {entries.map(([src, tgt], idx) => (
              <div key={idx} className="config-row">
                <input
                  type="text"
                  className="form-input config-input"
                  value={src}
                  onChange={(e) => {
                    const newMappings: Record<string, string> = {};
                    entries.forEach(([s, t], j) => {
                      newMappings[j === idx ? e.target.value : s] = t as string;
                    });
                    onChange({ ...config, mappings: newMappings });
                  }}
                  placeholder={t('admin.tradeImageCapture.sourceValue')}
                />
                <span style={{ color: '#64748b' }}>&rarr;</span>
                <input
                  type="text"
                  className="form-input config-input"
                  value={tgt as string}
                  onChange={(e) => {
                    const newMappings: Record<string, string> = {};
                    entries.forEach(([s, t], j) => {
                      newMappings[s] = j === idx ? e.target.value : (t as string);
                    });
                    onChange({ ...config, mappings: newMappings });
                  }}
                  placeholder={t('admin.tradeImageCapture.fxPlusValue')}
                />
                <button
                  className="btn-icon btn-icon-danger"
                  onClick={() => {
                    const newMappings = { ...currentMappings };
                    delete newMappings[src];
                    onChange({ ...config, mappings: newMappings });
                  }}
                >×</button>
              </div>
            ))}
            <button
              className="btn-secondary btn-sm"
              onClick={() => onChange({ ...config, mappings: { ...currentMappings, '': '' } })}
            >+ {t('admin.tradeImageCapture.addMapping')}</button>
          </div>
        );
      }
      case 'NUMBER_FORMAT':
        return (
          <div className="transform-config-editor">
            <div className="config-row">
              <label>{t('admin.tradeImageCapture.thousandSeparator')}:</label>
              <input
                type="text"
                className="form-input config-input-small"
                value={config.thousandSeparator || ''}
                onChange={(e) => onChange({ ...config, thousandSeparator: e.target.value })}
                placeholder="."
                maxLength={1}
              />
            </div>
            <div className="config-row">
              <label>{t('admin.tradeImageCapture.decimalSeparator')}:</label>
              <input
                type="text"
                className="form-input config-input-small"
                value={config.decimalSeparator || ''}
                onChange={(e) => onChange({ ...config, decimalSeparator: e.target.value })}
                placeholder=","
                maxLength={1}
              />
            </div>
          </div>
        );
      case 'TEXT_REPLACE':
        return (
          <div className="transform-config-editor">
            <div className="config-row">
              <label>{t('admin.tradeImageCapture.find')}:</label>
              <input
                type="text"
                className="form-input config-input"
                value={config.find || ''}
                onChange={(e) => onChange({ ...config, find: e.target.value })}
              />
            </div>
            <div className="config-row">
              <label>{t('admin.tradeImageCapture.replace')}:</label>
              <input
                type="text"
                className="form-input config-input"
                value={config.replace || ''}
                onChange={(e) => onChange({ ...config, replace: e.target.value })}
              />
            </div>
          </div>
        );
      case 'FIELD_SPLIT':
        return (
          <div className="transform-config-editor">
            <div className="config-row">
              <label>{t('admin.tradeImageCapture.delimiter')}:</label>
              <input
                type="text"
                className="form-input config-input-small"
                value={config.delimiter || ''}
                onChange={(e) => onChange({ ...config, delimiter: e.target.value })}
                placeholder="-"
                maxLength={5}
              />
            </div>
            <div className="config-row">
              <label>{t('admin.tradeImageCapture.partSelect')}:</label>
              <select
                className="form-select form-select-sm"
                value={config.partIndex ?? 0}
                onChange={(e) => onChange({ ...config, partIndex: parseInt(e.target.value) })}
              >
                {[0, 1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>
                    {t('admin.tradeImageCapture.partN', { n: n + 1 })}
                  </option>
                ))}
              </select>
            </div>
            <p className="tic-field-help" style={{ margin: '4px 0 0' }}>
              {t('admin.tradeImageCapture.fieldSplitHelp')}
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  // Helper to get field label
  const getFieldLabel = (fieldValue: string): string => {
    return fxPlusFields.find((f) => f.value === fieldValue)?.label || fieldValue;
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  // KAN-125: Multi-trade sub-tab — render MultiTradeConfigPanel instead
  if (subTab === 'multi') {
    return (
      <div className="tic-config-panel">
        <div className="sub-tabs">
          <button
            className="sub-tab-button"
            onClick={() => setSubTab('single')}
          >
            {t('admin.multiTradeCapture.subTabSingle')}
          </button>
          <button
            className="sub-tab-button active"
            onClick={() => setSubTab('multi')}
          >
            {t('admin.multiTradeCapture.subTabMultiple')}
          </button>
        </div>
        <MultiTradeConfigPanel />
      </div>
    );
  }

  if (mode === 'loading') {
    return (
      <div className="tic-config-panel">
        <div className="sub-tabs">
          <button className="sub-tab-button active" onClick={() => setSubTab('single')}>
            {t('admin.multiTradeCapture.subTabSingle')}
          </button>
          <button className="sub-tab-button" onClick={() => setSubTab('multi')}>
            {t('admin.multiTradeCapture.subTabMultiple')}
          </button>
        </div>
        <div className="tab-loading"><div className="spinner" /><p>{t('common.loading')}</p></div>
      </div>
    );
  }

  // Empty state
  if (mode === 'empty') {
    return (
      <div className="tic-config-panel">
        <div className="sub-tabs">
          <button className="sub-tab-button active" onClick={() => setSubTab('single')}>
            {t('admin.multiTradeCapture.subTabSingle')}
          </button>
          <button className="sub-tab-button" onClick={() => setSubTab('multi')}>
            {t('admin.multiTradeCapture.subTabMultiple')}
          </button>
        </div>
        <h3 className="tic-title">{t('admin.tradeImageCapture.title')}</h3>
        <div className="tic-empty-state">
          <h4>{t('admin.tradeImageCapture.emptyStateTitle')}</h4>
          <p>{t('admin.tradeImageCapture.emptyStateDescription')}</p>
          <div className="tic-empty-actions">
            <div
              className="trade-image-capture-zone tic-capture-zone"
              onClick={handleCaptureClick}
            >
              {t('admin.tradeImageCapture.pasteScreenshot')}
            </div>
          </div>
        </div>
        <AlertModal
          isOpen={alertModal.isOpen}
          onClose={() => setAlertModal((prev) => ({ ...prev, isOpen: false }))}
          type={alertModal.type}
          title={alertModal.title}
          message={alertModal.message}
        />
      </div>
    );
  }

  // Analysing state
  if (mode === 'analysing') {
    return (
      <div className="tic-config-panel">
        <div className="sub-tabs">
          <button className="sub-tab-button active" onClick={() => setSubTab('single')}>
            {t('admin.multiTradeCapture.subTabSingle')}
          </button>
          <button className="sub-tab-button" onClick={() => setSubTab('multi')}>
            {t('admin.multiTradeCapture.subTabMultiple')}
          </button>
        </div>
        <h3 className="tic-title">{t('admin.tradeImageCapture.title')}</h3>
        <div className="tic-analysing">
          <div className="capture-spinner" />
          <span>{t('admin.tradeImageCapture.analysing')}</span>
        </div>
        {capturedImageDataUrl && (
          <div className="tic-analysing-image">
            <img src={capturedImageDataUrl} alt="Analysing" />
          </div>
        )}
      </div>
    );
  }

  // Draft review or Edit mode — show image panel whenever we have an image
  const hasCapturedImage = !!capturedImageDataUrl && (mode === 'draft' || mode === 'edit');

  return (
    <div className="tic-config-panel">
      <div className="sub-tabs">
        <button className="sub-tab-button active" onClick={() => setSubTab('single')}>
          {t('admin.multiTradeCapture.subTabSingle')}
        </button>
        <button className="sub-tab-button" onClick={() => setSubTab('multi')}>
          {t('admin.multiTradeCapture.subTabMultiple')}
        </button>
      </div>
      <h3 className="tic-title">{t('admin.tradeImageCapture.title')}</h3>

      {/* Analysis complete banner */}
      {mode === 'draft' && (
        <div className="tic-analysis-banner">
          {t('admin.tradeImageCapture.analysisComplete')}
        </div>
      )}

      {/* Split view (draft) or full-width (edit) */}
      <div className={`tic-content ${hasCapturedImage ? 'tic-split' : 'tic-full'}`}>
        {/* Left: Image panel (draft mode with fresh capture, or edit mode with persisted image) */}
        {hasCapturedImage && (
          <div className="tic-image-panel">
            {/* Source System Name — above image */}
            <div className="tic-field-group">
              <label className="tic-field-label">{t('admin.tradeImageCapture.sourceSystemName')}</label>
              <input
                type="text"
                className="form-input"
                value={sourceSystemName}
                onChange={(e) => setSourceSystemName(e.target.value)}
                placeholder={t('admin.tradeImageCapture.sourceSystemPlaceholder')}
              />
            </div>

            <div className="tic-image-container">
              <img
                ref={imageRef}
                src={capturedImageDataUrl!}
                alt="Source system"
                onLoad={handleImageLoad}
                className="tic-source-image"
              />
              <ImageHighlightOverlay
                highlights={highlights}
                imageWidth={displayedImageSize.w}
                imageHeight={displayedImageSize.h}
                naturalWidth={naturalImageSize.w}
                naturalHeight={naturalImageSize.h}
                onClick={handleFieldClick}
              />
            </div>
            {mode === 'draft' && (
              <div
                className="trade-image-capture-zone tic-capture-zone tic-recapture"
                onClick={handleCaptureClick}
              >
                {t('admin.tradeImageCapture.pasteNewScreenshot')}
              </div>
            )}
          </div>
        )}

        {/* Right (or full-width): Config form */}
        <div className="tic-form-panel">

          {/* Source System Name — in form panel when no image */}
          {!hasCapturedImage && (
            <div className="tic-field-group">
              <label className="tic-field-label">{t('admin.tradeImageCapture.sourceSystemName')}</label>
              <input
                type="text"
                className="form-input"
                value={sourceSystemName}
                onChange={(e) => setSourceSystemName(e.target.value)}
                placeholder={t('admin.tradeImageCapture.sourceSystemPlaceholder')}
              />
            </div>
          )}

          {/* Field Mappings (with inline transforms) */}
          <div className="tic-section">
            <div className="tic-section-header">
              <h4>{t('admin.tradeImageCapture.fieldMappings')}</h4>
              <button className="btn-secondary btn-sm" onClick={() => setShowAddMapping(true)}>
                {t('admin.tradeImageCapture.addMapping')}
              </button>
            </div>

            <div className="tic-mappings-table">
              <div className="tic-table-header">
                <span>{t('admin.tradeImageCapture.sourceLabel')}</span>
                <span>{t('admin.tradeImageCapture.targetField')}</span>
                <span>{t('admin.tradeImageCapture.transformType')}</span>
                <span>{t('admin.tradeImageCapture.confidence')}</span>
                <span>{t('admin.tradeImageCapture.action')}</span>
              </div>
              {mappingRows.map((row, i) => (
                <React.Fragment key={i}>
                  <div
                    className={`tic-table-row ${selectedField === row.sourceLabel ? 'selected' : ''} ${row.isManual ? 'manual' : ''} ${row.confirmed ? 'confirmed' : ''}`}
                    onClick={() => row.coordinates && handleFieldClick(row.sourceLabel)}
                  >
                    <span className="tic-cell-source">
                      {row.sourceLabel}
                      {row.isManual && <span className="tic-manual-badge">{t('admin.tradeImageCapture.manual')}</span>}
                    </span>
                    <span
                      className="tic-cell-target tic-cell-editable"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (row.coordinates) handleFieldClick(row.sourceLabel);
                        if (editingMappingIndex !== i) setEditingMappingIndex(i);
                      }}
                    >
                      {editingMappingIndex === i ? (
                        <select
                          className="form-select form-select-sm"
                          value={row.targetField}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            updateMappingTarget(i, e.target.value);
                            setEditingMappingIndex(null);
                          }}
                          onBlur={() => setEditingMappingIndex(null)}
                          autoFocus
                        >
                          {fxPlusFields.map((f) => (
                            <option key={f.value} value={f.value}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        getFieldLabel(row.targetField)
                      )}
                    </span>
                    <span className="tic-cell-transform">
                      {row.transformType ? (
                        <span
                          className="tic-transform-pill"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (row.coordinates) handleFieldClick(row.sourceLabel);
                            setExpandedRowIndex(expandedRowIndex === i ? null : i);
                          }}
                        >
                          {TRANSFORM_TYPES.find((tt) => tt.value === row.transformType)?.label || row.transformType}
                        </span>
                      ) : (
                        <button
                          className="btn-icon tic-add-transform-btn"
                          title={t('admin.tradeImageCapture.addTransform')}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (row.coordinates) handleFieldClick(row.sourceLabel);
                            updateRowTransform(i, 'DATE_FORMAT', {});
                            setExpandedRowIndex(i);
                          }}
                        >
                          +
                        </button>
                      )}
                    </span>
                    <span className="tic-cell-confidence">
                      {row.confirmed ? (
                        <span className="tic-confidence-badge high">OK</span>
                      ) : !row.isManual ? (
                        <span className={`tic-confidence-badge ${row.confidence >= 0.9 ? 'high' : row.confidence >= 0.6 ? 'medium' : 'low'}`}>
                          {Math.round(row.confidence * 100)}%
                        </span>
                      ) : null}
                    </span>
                    <span className="tic-cell-actions" onClick={(e) => {
                        e.stopPropagation();
                        if (row.coordinates) handleFieldClick(row.sourceLabel);
                      }}>
                      <button
                        className={`btn-icon ${row.confirmed ? 'tic-confirmed-btn' : 'tic-confirm-btn'}`}
                        title={row.confirmed ? t('admin.tradeImageCapture.confirmed') : t('admin.tradeImageCapture.confirm')}
                        onClick={() => toggleConfirmed(i)}
                      >
                        ✓
                      </button>
                      <button
                        className="btn-icon"
                        title={t('admin.tradeImageCapture.duplicateRow')}
                        onClick={() => duplicateMapping(i)}
                      >
                        ⧉
                      </button>
                      <button
                        className="btn-icon btn-icon-danger"
                        title={t('admin.tradeImageCapture.remove')}
                        onClick={() => removeMapping(i)}
                      >
                        ×
                      </button>
                    </span>
                  </div>

                  {/* Expanded transform config (inline below the row) */}
                  {expandedRowIndex === i && row.transformType && (
                    <div className="tic-row-expand">
                      <div className="tic-row-expand-header">
                        <select
                          className="form-select form-select-sm"
                          value={row.transformType}
                          onChange={(e) => updateRowTransform(i, e.target.value, {})}
                        >
                          {TRANSFORM_TYPES.map((tt) => (
                            <option key={tt.value} value={tt.value}>
                              {tt.label}
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn-icon"
                          title={t('common.close')}
                          onClick={() => setExpandedRowIndex(null)}
                        >
                          ▲
                        </button>
                      </div>
                      {renderTransformConfig(
                        row.transformType,
                        row.transformConfig || {},
                        (newConfig) => updateRowTransform(i, row.transformType, newConfig),
                        row.targetField,
                      )}
                      <div className="tic-row-expand-footer">
                        <button
                          className="btn-primary btn-sm"
                          onClick={() => setExpandedRowIndex(null)}
                        >
                          {t('common.ok')}
                        </button>
                        {row.transformType === 'FIELD_SPLIT' && (
                          <button
                            className="btn-secondary btn-sm"
                            onClick={() => duplicateMapping(i)}
                          >
                            {t('admin.tradeImageCapture.duplicateForSplit')}
                          </button>
                        )}
                        <button
                          className="btn-secondary btn-sm tic-remove-transform-btn"
                          onClick={() => {
                            updateRowTransform(i, undefined, undefined);
                            setExpandedRowIndex(null);
                          }}
                        >
                          {t('admin.tradeImageCapture.removeTransform')}
                        </button>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}

              {mappingRows.length === 0 && (
                <div className="tic-table-empty">{t('admin.tradeImageCapture.noMappings')}</div>
              )}
            </div>

            {/* Add mapping inline form */}
            {showAddMapping && (
              <div className="tic-add-form">
                <input
                  type="text"
                  className="form-input"
                  value={newMappingSource}
                  onChange={(e) => setNewMappingSource(e.target.value)}
                  placeholder={t('admin.tradeImageCapture.sourceLabel')}
                />
                <select
                  className="form-select"
                  value={newMappingTarget}
                  onChange={(e) => setNewMappingTarget(e.target.value)}
                >
                  <option value="">{t('admin.tradeImageCapture.targetField')}</option>
                  {fxPlusFields.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-input"
                  value={newMappingDescription}
                  onChange={(e) => setNewMappingDescription(e.target.value)}
                  placeholder={t('admin.tradeImageCapture.description')}
                />
                <div className="tic-add-form-actions">
                  <button className="btn-primary btn-sm" onClick={addMapping}>
                    {t('admin.tradeImageCapture.addMapping')}
                  </button>
                  <button className="btn-secondary btn-sm" onClick={() => setShowAddMapping(false)}>
                    {t('admin.tradeImageCapture.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="tic-actions">
            <button
              className="btn-primary"
              onClick={handleSave}
              disabled={saving || deleting}
            >
              {saving ? '...' : t('admin.tradeImageCapture.save')}
            </button>
            {mode === 'edit' && (
              <button
                className="btn-danger"
                onClick={confirmDelete}
                disabled={saving || deleting}
              >
                {deleting ? '...' : t('admin.tradeImageCapture.deleteMapping')}
              </button>
            )}
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal((prev) => ({ ...prev, isOpen: false }))}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default TradeImageCaptureConfigPanel;
